<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import WahaInboxesAPI from 'dashboard/api/wahaInboxes';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const router = useRouter();

const inboxName = ref('');
const isCreating = ref(false);
const inboxId = ref(null);
const qr = ref(null);
const status = ref(null);
const connected = ref(false);
let pollTimer = null;

const clearPoll = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

onBeforeUnmount(clearPoll);

const goToAgents = () => {
  clearPoll();
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: inboxId.value },
  });
};

const poll = async () => {
  try {
    const { data } = await WahaInboxesAPI.getStatus(inboxId.value);
    status.value = data.status;
    qr.value = data.qr || qr.value;
    if (data.connected) {
      connected.value = true;
      setTimeout(goToAgents, 1200);
      return;
    }
  } catch (e) {
    // reintenta igual; WAHA puede tardar en levantar la sesión
  }
  pollTimer = setTimeout(poll, 3000);
};

const createInbox = async () => {
  if (!inboxName.value.trim()) return;
  try {
    isCreating.value = true;
    const { data } = await WahaInboxesAPI.createInbox(inboxName.value.trim());
    inboxId.value = data.inbox_id;
    status.value = data.status;
    qr.value = data.qr;
    if (data.connected) {
      connected.value = true;
      setTimeout(goToAgents, 1200);
    } else {
      poll();
    }
  } catch (error) {
    const msg = error?.response?.data?.error || 'No se pudo crear la bandeja WAHA';
    useAlert(msg);
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      header-title="WhatsApp (WAHA)"
      header-content="Crea una bandeja de WhatsApp por WAHA y escanea el QR desde aquí. Solo administradores."
    />

    <form
      v-if="!inboxId"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createInbox()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          Nombre de la bandeja
          <input
            v-model="inboxName"
            type="text"
            placeholder="Ej. WhatsApp Santo Domingo Este"
          />
        </label>
      </div>
      <div class="w-full mt-4">
        <NextButton
          :is-loading="isCreating"
          :disabled="!inboxName.trim()"
          type="submit"
          solid
          blue
          label="Crear y conectar"
        />
      </div>
    </form>

    <div v-else class="flex flex-col items-center gap-4 mt-4">
      <div
        v-if="connected"
        class="text-base font-medium text-green-600 dark:text-green-400"
      >
        ✅ Conectado — preparando la bandeja…
      </div>

      <template v-else>
        <p class="text-sm text-n-slate-11 text-center max-w-md">
          Abre WhatsApp en el teléfono →
          <strong>Dispositivos vinculados</strong> →
          <strong>Vincular un dispositivo</strong> y escanea este código.
          Mantén el teléfono con internet y WhatsApp abierto.
        </p>
        <div
          class="flex items-center justify-center w-64 h-64 border rounded-lg bg-white"
        >
          <img
            v-if="qr"
            :src="qr"
            alt="QR WhatsApp"
            class="w-60 h-60 object-contain"
          />
          <span v-else class="text-sm text-n-slate-10">
            Generando QR… (estado: {{ status || 'iniciando' }})
          </span>
        </div>
        <div class="text-xs text-n-slate-10">
          Estado: {{ status || 'iniciando' }} · se actualiza solo
        </div>
      </template>
    </div>
  </div>
</template>
